/**
 * Procedural skin (W2-01b; ARCHITECTURE.md §12, skin-and-assets.md S§1.3, S§3, S§4, S§8): the skin the editor
 * draws itself, so the preview never depends on proprietary art. Every image has the Phigros skin's pixel size and
 * aspect, so note quads, hold slices, the MH scale and the hit effect come out exactly as large as with the Phigros
 * skin:
 *
 *   click 989x100, drag 989x60, flick 989x200, hold 989x2000 (holdAtlas [50, 50]: tail rows 0-49 on top, body,
 *   head rows 1950-1999 at the bottom), click_mh 1089x200, drag_mh 1089x160, flick_mh 1089x300 (the normal note
 *   centred with a 50 px glow margin), hold_mh 1056x1997 (holdAtlasMH [50, 95]: the glow stays inside the head
 *   slice), hit_fx 1280x1536 = 5 x 6 frames of 256x256 (white, so Phira's tint applies; the last frame is nearly
 *   empty).
 *
 * The art is flat bars in the classic note colours (tap blue with end chevrons, drag yellow, flick red with an
 * upward arrow, hold blue with a translucent body). These are pixel colours of a game texture, not UI chrome, so
 * they are constants here rather than design tokens. Sounds are synthesised (proceduralSounds.ts).
 *
 * The bitmaps are straight alpha, like the decoded respack images (decodeSkin.ts; render/types.ts: the renderer
 * blends with SRC_ALPHA/ONE_MINUS_SRC_ALPHA as Phira does): a canvas stores premultiplied pixels, so every image is
 * taken out with `createImageBitmap(canvas, { premultiplyAlpha: 'none' })`, which un-premultiplies. Never
 * `transferToImageBitmap()`: its bitmap stays premultiplied, and WebGL ignores UNPACK_PREMULTIPLY_ALPHA_WEBGL for
 * ImageBitmap sources, so translucent pixels (the hold body, rims, MH glows, the hit effect) would come out dark.
 * The one-pixel placeholder images are opaque, so their alpha mode does not matter.
 */

import type { SkinMetrics } from '../../core/engine/types';
import { skinMetricsFrom } from '../../formats/respack/metrics';
import { RESPACK_IMAGES, type RespackImageName, type ResPackInfo } from '../../formats/respack/types';
import { RESPACK_DEFAULTS } from '../../formats/respack/validate';
import type { LoadedSkin } from '../types';
import { assembleSkin, type ImageSize, type SkinSounds } from './decodeSkin';
import { synthesizeSkinSounds } from './proceduralSounds';

/** Pixel sizes of the Phigros Note Skin's images (S§1.3), shared by the procedural and placeholder skins. */
export const PHIGROS_IMAGE_SIZES: Readonly<Record<RespackImageName, ImageSize>> = {
  click: [989, 100],
  hold: [989, 2000],
  flick: [989, 200],
  drag: [989, 60],
  click_mh: [1089, 200],
  hold_mh: [1056, 1997],
  flick_mh: [1089, 300],
  drag_mh: [1089, 160],
  hit_fx: [1280, 1536],
};

export const PROCEDURAL_SKIN_ID = 'procedural';

/** info.yml of the procedural skin: the Phigros skin's atlas and grid, Phira's defaults for everything else. */
export const PROCEDURAL_SKIN_INFO: ResPackInfo = {
  name: 'Phiedit Classic',
  author: 'Phiedit',
  description: '',
  hitFx: [5, 6],
  hitFxDuration: RESPACK_DEFAULTS.hitFxDuration,
  hitFxScale: RESPACK_DEFAULTS.hitFxScale,
  hitFxRotate: RESPACK_DEFAULTS.hitFxRotate,
  hitFxTinted: RESPACK_DEFAULTS.hitFxTinted,
  hideParticles: RESPACK_DEFAULTS.hideParticles,
  holdAtlas: [50, 50],
  holdAtlasMH: [50, 95],
  holdKeepHead: RESPACK_DEFAULTS.holdKeepHead,
  holdRepeat: RESPACK_DEFAULTS.holdRepeat,
  holdCompact: RESPACK_DEFAULTS.holdCompact,
  colorPerfect: RESPACK_DEFAULTS.colorPerfect,
  colorGood: RESPACK_DEFAULTS.colorGood,
};

/** SkinMetrics of the procedural skin (= the Phigros skin's): also the placeholder skin's metrics (S§3.6). */
export const PROCEDURAL_METRICS: SkinMetrics = skinMetricsFrom(PROCEDURAL_SKIN_INFO, PHIGROS_IMAGE_SIZES);

// ---------------------------------------------------------------------------------------------------------------
// Art
// ---------------------------------------------------------------------------------------------------------------

/** The Canvas 2D subset the art uses (CanvasRenderingContext2D and OffscreenCanvasRenderingContext2D both fit). */
export type Paint2D = Pick<
  CanvasRenderingContext2D,
  | 'save'
  | 'restore'
  | 'translate'
  | 'rotate'
  | 'beginPath'
  | 'closePath'
  | 'moveTo'
  | 'lineTo'
  | 'arc'
  | 'rect'
  | 'clip'
  | 'fill'
  | 'stroke'
  | 'fillRect'
  | 'strokeRect'
  | 'clearRect'
  | 'createLinearGradient'
  | 'createRadialGradient'
  | 'fillStyle'
  | 'strokeStyle'
  | 'lineWidth'
  | 'lineCap'
  | 'lineJoin'
  | 'globalAlpha'
  | 'shadowBlur'
  | 'shadowColor'
>;

interface BarStyle {
  /** Outline colour. */
  readonly rim: string;
  /** Fill gradient, far edge (image top) to near edge (image bottom). */
  readonly top: string;
  readonly bottom: string;
}

/** Note texture colours (game art, see the file header). */
export const SKIN_ART = {
  tap: { rim: '#f4fdff', top: '#79dcff', bottom: '#119ae6' },
  drag: { rim: '#fffbe8', top: '#fff07a', bottom: '#f0bf12' },
  flick: { rim: '#fff3f6', top: '#ff8aa3', bottom: '#e3204a' },
  holdTail: { rim: '#f4fdff', top: '#d2f4ff', bottom: '#86dcff' },
  holdBodyFar: 'rgba(96, 205, 255, 0.5)',
  holdBodyNear: 'rgba(24, 158, 238, 0.82)',
  holdEdge: 'rgba(244, 253, 255, 0.92)',
  glyph: '#ffffff',
  glow: '#ffe27a',
  glowCore: '#fff6c8',
  hitFx: '#ffffff',
} as const satisfies Readonly<Record<string, BarStyle | string>>;

/** One-pixel colours of the placeholder skin (shown only until the real skin is ready). */
export const PLACEHOLDER_COLOURS: Readonly<Record<RespackImageName, string>> = {
  click: SKIN_ART.tap.bottom,
  hold: SKIN_ART.tap.bottom,
  flick: SKIN_ART.flick.bottom,
  drag: SKIN_ART.drag.bottom,
  click_mh: SKIN_ART.tap.bottom,
  hold_mh: SKIN_ART.tap.bottom,
  flick_mh: SKIN_ART.flick.bottom,
  drag_mh: SKIN_ART.drag.bottom,
  hit_fx: SKIN_ART.hitFx,
};

/** Outline thickness of a bar of height h (8 px on a tap). */
function rimOf(h: number): number {
  return Math.max(4, Math.min(8, Math.round(h * 0.08)));
}

/** A flat bar: rim, gradient fill. (x, y) is the image-space top-left (y down = toward the judge line). */
function drawBar(ctx: Paint2D, x: number, y: number, w: number, h: number, style: BarStyle): void {
  const rim = rimOf(h);
  ctx.fillStyle = style.rim;
  ctx.fillRect(x, y, w, h);
  const g = ctx.createLinearGradient(0, y + rim, 0, y + h - rim);
  g.addColorStop(0, style.top);
  g.addColorStop(1, style.bottom);
  ctx.fillStyle = g;
  ctx.fillRect(x + rim * 1.5, y + rim, w - rim * 3, h - rim * 2);
}

/** A chevron pointing left, right or up, centred on (cx, cy), `size` = its full extent along the pointing axis. */
function drawChevron(
  ctx: Paint2D,
  cx: number,
  cy: number,
  size: number,
  dir: 'left' | 'right' | 'up',
  width: number,
): void {
  const half = size / 2;
  ctx.beginPath();
  if (dir === 'up') {
    ctx.moveTo(cx - size, cy + half);
    ctx.lineTo(cx, cy - half);
    ctx.lineTo(cx + size, cy + half);
  } else {
    const s = dir === 'left' ? 1 : -1;
    ctx.moveTo(cx + s * half, cy - size * 0.9);
    ctx.lineTo(cx - s * half, cy);
    ctx.lineTo(cx + s * half, cy + size * 0.9);
  }
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = SKIN_ART.glyph;
  ctx.stroke();
}

function drawTap(ctx: Paint2D, x: number, y: number, w: number, h: number): void {
  drawBar(ctx, x, y, w, h, SKIN_ART.tap);
  // Double chevrons near both ends: << at the left, >> at the right.
  const size = h * 0.28;
  const lw = Math.max(3, h * 0.07);
  for (const k of [0, 1]) {
    drawChevron(ctx, x + w * 0.045 + k * size * 0.9, y + h / 2, size, 'left', lw);
    drawChevron(ctx, x + w * 0.955 - k * size * 0.9, y + h / 2, size, 'right', lw);
  }
}

function drawDrag(ctx: Paint2D, x: number, y: number, w: number, h: number): void {
  drawBar(ctx, x, y, w, h, SKIN_ART.drag);
  // A thin highlight line along the middle.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
  ctx.fillRect(x + w * 0.06, y + h * 0.42, w * 0.88, Math.max(2, h * 0.08));
}

function drawFlick(ctx: Paint2D, x: number, y: number, w: number, h: number): void {
  drawBar(ctx, x, y, w, h, SKIN_ART.flick);
  // Two stacked upward chevrons in the middle (the flick direction mark).
  const size = h * 0.2;
  const lw = Math.max(4, h * 0.075);
  drawChevron(ctx, x + w / 2, y + h * 0.37, size, 'up', lw);
  drawChevron(ctx, x + w / 2, y + h * 0.63, size, 'up', lw);
}

/** Soft gold glow behind the rectangle (multi-highlight variants). */
function drawGlow(ctx: Paint2D, x: number, y: number, w: number, h: number, pad: number): void {
  ctx.save();
  ctx.shadowColor = SKIN_ART.glow;
  ctx.shadowBlur = pad * 0.85;
  ctx.fillStyle = SKIN_ART.glow;
  for (let i = 0; i < 2; i++) ctx.fillRect(x - pad * 0.18, y - pad * 0.18, w + pad * 0.36, h + pad * 0.36);
  ctx.restore();
  ctx.fillStyle = SKIN_ART.glowCore;
  ctx.fillRect(x - pad * 0.12, y - pad * 0.12, w + pad * 0.24, h + pad * 0.24);
}

/**
 * A hold image: tail slice rows [0, tail), body rows [tail, H - head), head slice rows [H - head, H) (S§3.4). The
 * head cap is `capPx` rows at the top of the head slice; the rest of the slice (MH) is glow below the cap. The note
 * is centred horizontally (x on both sides), so the image is `2x + w` wide.
 */
function drawHold(
  ctx: Paint2D,
  x: number,
  w: number,
  h: number,
  atlas: readonly [number, number],
  capPx: number,
  glowPad: number,
): void {
  const [tail, head] = atlas;
  const bodyTop = tail;
  const bodyBottom = h - head;
  // Body: translucent, brighter toward the head, with white side edges.
  const g = ctx.createLinearGradient(0, bodyTop, 0, bodyBottom);
  g.addColorStop(0, SKIN_ART.holdBodyFar);
  g.addColorStop(1, SKIN_ART.holdBodyNear);
  ctx.fillStyle = g;
  ctx.fillRect(x, bodyTop, w, bodyBottom - bodyTop);
  const edge = 8;
  ctx.fillStyle = SKIN_ART.holdEdge;
  ctx.fillRect(x, bodyTop, edge, bodyBottom - bodyTop);
  ctx.fillRect(x + w - edge, bodyTop, edge, bodyBottom - bodyTop);
  // Tail: a lighter cap at the far end.
  drawBar(ctx, x, 0, w, tail, SKIN_ART.holdTail);
  // Head: a tap-like cap right below the body. The MH glow is clipped to the head slice: the body slice is stretched
  // (or tiled) along the whole hold, so any glow that bled into its last rows would tint every long hold body.
  if (glowPad > 0) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, bodyBottom, x * 2 + w, head);
    ctx.clip();
    drawGlow(ctx, x, bodyBottom, w, capPx, glowPad);
    ctx.restore();
  }
  drawBar(ctx, x, bodyBottom, w, capPx, SKIN_ART.tap);
}

/** Ease-out cubic. */
function easeOut(t: number): number {
  return 1 - (1 - t) ** 3;
}

/**
 * One hit-effect frame centred at (cx, cy) with radius R, at animation progress t in [0, 1): a bright core that fades
 * fast, an expanding ring, a rotating square frame and four arcs, all white (Phira tints the sprite).
 */
export function drawHitFxFrame(ctx: Paint2D, cx: number, cy: number, R: number, t: number): void {
  const e = easeOut(t);
  const fade = (1 - t) ** 1.4;
  ctx.save();
  ctx.translate(cx, cy);
  // Core glow.
  const coreAlpha = Math.max(0, 1 - t * 2.4);
  if (coreAlpha > 0) {
    const rg = ctx.createRadialGradient(0, 0, 0, 0, 0, R * (0.3 + 0.25 * e));
    rg.addColorStop(0, `rgba(255, 255, 255, ${(0.95 * coreAlpha).toFixed(3)})`);
    rg.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = rg;
    ctx.fillRect(-R, -R, R * 2, R * 2);
  }
  ctx.strokeStyle = SKIN_ART.hitFx;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'miter';
  // Expanding ring.
  ctx.globalAlpha = 0.9 * fade;
  ctx.lineWidth = Math.max(1, R * 0.07 * (1 - t));
  ctx.beginPath();
  ctx.arc(0, 0, R * (0.22 + 0.62 * e), 0, Math.PI * 2);
  ctx.stroke();
  // Rotating square frame.
  ctx.globalAlpha = fade;
  ctx.save();
  ctx.rotate(Math.PI / 4 + (Math.PI / 3) * e);
  const half = R * (0.24 + 0.42 * e);
  ctx.lineWidth = Math.max(1, R * 0.1 * (1 - t));
  ctx.strokeRect(-half, -half, half * 2, half * 2);
  ctx.restore();
  // Four arcs flying outward.
  ctx.globalAlpha = 0.85 * fade;
  ctx.lineWidth = Math.max(1, R * 0.045 * (1 - t));
  const arcR = R * (0.45 + 0.5 * e);
  for (let i = 0; i < 4; i++) {
    const a0 = (i * Math.PI) / 2 + e * 0.9;
    ctx.beginPath();
    ctx.arc(0, 0, arcR, a0, a0 + 0.55 * (1 - t * 0.6));
    ctx.stroke();
  }
  ctx.restore();
  ctx.globalAlpha = 1;
}

/** Draws image `name` of the procedural skin into a context of the image's size (PHIGROS_IMAGE_SIZES). */
export function drawProceduralImage(ctx: Paint2D, name: RespackImageName): void {
  const [W, H] = PHIGROS_IMAGE_SIZES[name];
  const [nw] = PHIGROS_IMAGE_SIZES.click;
  ctx.clearRect(0, 0, W, H);
  switch (name) {
    case 'click':
      drawTap(ctx, 0, 0, W, H);
      return;
    case 'drag':
      drawDrag(ctx, 0, 0, W, H);
      return;
    case 'flick':
      drawFlick(ctx, 0, 0, W, H);
      return;
    case 'hold':
      drawHold(ctx, 0, W, H, PROCEDURAL_SKIN_INFO.holdAtlas, PROCEDURAL_SKIN_INFO.holdAtlas[1], 0);
      return;
    case 'click_mh':
    case 'drag_mh':
    case 'flick_mh': {
      const base = name === 'click_mh' ? 'click' : name === 'drag_mh' ? 'drag' : 'flick';
      const [bw, bh] = PHIGROS_IMAGE_SIZES[base];
      const x = (W - bw) / 2;
      const y = (H - bh) / 2;
      drawGlow(ctx, x, y, bw, bh, x);
      if (base === 'click') drawTap(ctx, x, y, bw, bh);
      else if (base === 'drag') drawDrag(ctx, x, y, bw, bh);
      else drawFlick(ctx, x, y, bw, bh);
      return;
    }
    case 'hold_mh': {
      const x = (W - nw) / 2;
      const [tail, head] = PROCEDURAL_SKIN_INFO.holdAtlasMH;
      // The head slice holds a cap as tall as the normal hold's head, plus glow below it.
      const cap = PROCEDURAL_SKIN_INFO.holdAtlas[1];
      drawHold(ctx, x, nw, H, [tail, head], cap, Math.max(x, head - cap));
      return;
    }
    case 'hit_fx': {
      const [cols, rows] = PROCEDURAL_SKIN_INFO.hitFx;
      const fw = W / cols;
      const fh = H / rows;
      const n = cols * rows;
      for (let i = 0; i < n; i++) {
        const cx = (i % cols) * fw + fw / 2;
        const cy = Math.floor(i / cols) * fh + fh / 2;
        ctx.save();
        ctx.beginPath();
        ctx.rect(cx - fw / 2, cy - fh / 2, fw, fh);
        ctx.clip();
        drawHitFxFrame(ctx, cx, cy, Math.min(fw, fh) / 2, (i + 0.5) / n);
        ctx.restore();
      }
      return;
    }
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Canvases and bitmaps
// ---------------------------------------------------------------------------------------------------------------

export interface Surface {
  readonly ctx: Paint2D;
  /** The drawing as a straight-alpha bitmap (call once: the canvas is released afterwards). */
  toBitmap(): Promise<ImageBitmap>;
}

/**
 * How a drawing becomes a texture: straight alpha like decodeSkin's images (see the file header), no colour-space
 * conversion (the colours are the texture's pixel values).
 */
export const SURFACE_BITMAP_OPTIONS: Readonly<ImageBitmapOptions> = {
  premultiplyAlpha: 'none',
  colorSpaceConversion: 'none',
};

/** A straight-alpha copy of the canvas; the canvas's backing store is freed right after (up to 8 MB per image). */
async function straightBitmap(canvas: OffscreenCanvas | HTMLCanvasElement): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(canvas, SURFACE_BITMAP_OPTIONS);
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}

/** An OffscreenCanvas (preferred) or a detached <canvas> of the given size. */
export function createSurface(w: number, h: number): Surface {
  if (typeof OffscreenCanvas === 'function') {
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new Error('2D canvas unavailable');
    return { ctx, toBitmap: () => straightBitmap(canvas) };
  }
  if (typeof document === 'object') {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new Error('2D canvas unavailable');
    return { ctx, toBitmap: () => straightBitmap(canvas) };
  }
  throw new Error('No canvas available to draw the skin');
}

/** Lets the page breathe between large drawings at startup. */
function yieldToEventLoop(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/** Draws the nine procedural images (one per event-loop turn). */
export async function drawProceduralImages(): Promise<Record<RespackImageName, ImageBitmap>> {
  const images = {} as Record<RespackImageName, ImageBitmap>;
  const done: ImageBitmap[] = [];
  try {
    for (const name of RESPACK_IMAGES) {
      const [w, h] = PHIGROS_IMAGE_SIZES[name];
      const surface = createSurface(w, h);
      drawProceduralImage(surface.ctx, name);
      const bitmap = await surface.toBitmap();
      done.push(bitmap);
      images[name] = bitmap;
      await yieldToEventLoop();
    }
  } catch (e) {
    for (const b of done) b.close();
    throw e;
  }
  return images;
}

export interface ProceduralSkinOptions {
  /** Synthesised hit sounds (default: proceduralSounds.ts with OfflineAudioContext). */
  readonly sounds?: () => Promise<SkinSounds>;
}

/** Builds the procedural skin (images + synthesised sounds). */
export async function createProceduralSkin(opts: ProceduralSkinOptions = {}): Promise<LoadedSkin> {
  const soundsJob = (opts.sounds ?? synthesizeSkinSounds)().catch((): SkinSounds => ({
    click: null,
    drag: null,
    flick: null,
  }));
  const images = await drawProceduralImages();
  const sounds = await soundsJob;
  return assembleSkin({
    identity: { id: PROCEDURAL_SKIN_ID, source: 'procedural' },
    info: PROCEDURAL_SKIN_INFO,
    sizes: PHIGROS_IMAGE_SIZES,
    images,
    holdBody: null,
    holdBodyMh: null,
    sounds,
  });
}

/** The opaque RGBA bytes of a `#rrggbb` colour. */
export function rgbaOfHex(hex: string): Uint8ClampedArray<ArrayBuffer> {
  const n = /^#([0-9a-f]{6})$/i.exec(hex) === null ? 0 : Number.parseInt(hex.slice(1), 16);
  return new Uint8ClampedArray([(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff, 0xff]);
}

/**
 * A one-pixel texture source of an opaque colour, made synchronously (the placeholder skin is read on the boot path,
 * before anything could be awaited). An ImageBitmap where OffscreenCanvas exists; otherwise (Safari before 16.4) a
 * detached one-pixel `<canvas>`, else (no DOM, e.g. a worker) an ImageData, else (a runtime without image APIs, where
 * nothing draws anyway) a plain record of the pixel. WebGL uploads a canvas or an ImageData like an ImageBitmap
 * (texImage2D takes both), and every fallback gets the `width`/`height`/`close()` of an ImageBitmap (which is all of
 * ImageBitmap's interface), so reading the placeholder skin never throws.
 */
function onePixel(colour: string): ImageBitmap {
  if (typeof OffscreenCanvas === 'function') {
    const canvas = new OffscreenCanvas(1, 1);
    const ctx = canvas.getContext('2d');
    if (ctx !== null) {
      ctx.fillStyle = colour;
      ctx.fillRect(0, 0, 1, 1);
    }
    return canvas.transferToImageBitmap();
  }
  if (typeof document === 'object') {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    if (ctx !== null) {
      ctx.fillStyle = colour;
      ctx.fillRect(0, 0, 1, 1);
    }
    const close = (): void => {
      canvas.width = 0;
      canvas.height = 0;
    };
    return Object.assign(canvas, { close });
  }
  const data = rgbaOfHex(colour);
  const pixel: Pick<ImageData, 'data' | 'width' | 'height'> =
    typeof ImageData === 'function' ? new ImageData(data, 1, 1) : { data, width: 1, height: 1 };
  return Object.assign(pixel, { close: (): void => undefined });
}

/** One-pixel images in the note colours (placeholder skin; synchronous, see onePixel). */
export function placeholderImages(): Record<RespackImageName, ImageBitmap> {
  const images = {} as Record<RespackImageName, ImageBitmap>;
  for (const name of RESPACK_IMAGES) images[name] = onePixel(PLACEHOLDER_COLOURS[name]);
  return images;
}
